// Row-model helpers for the folder-aware SheetSelector types ('roles',
// 'roleFolders'). Pure functions — SheetSelector owns the expansion/scope/
// search state and calls into here to derive the flat row list fed into the
// list. Keeping this pure keeps the component lean and the tree logic testable.

// Per-role display counts. Members come from the `People:RolePeople(count)`
// embed, permissions from `Permissions(id)` (locked perms excluded — same rule
// as RoleCard on the roles screen), resources from the share join-table
// `BoardRoles(count)/CalendarRoles(count)/FolderRoles(count)` embeds on the
// roles list queries — message boards + calendars + library folders shared
// with the role, matching the role detail screen's resource definition. The
// join tables carry only org-scoped RLS, so counting them is far cheaper than
// counting the resource tables (per-row access functions) directly.
export const roleCounts = (role) => ({
    members: role?.People?.[0]?.count ?? 0,
    permissions: (role?.Permissions || []).filter(p => !p.locked).length,
    resources: (role?.BoardRoles?.[0]?.count || 0) + (role?.CalendarRoles?.[0]?.count || 0) + (role?.FolderRoles?.[0]?.count || 0),
});

// Normalizes query.data into a flat {folders, roles} pair.
// Accepts the getRolesInFolders shape ({children, Roles, allRoles, allFolders}),
// a flat roles array (roles pickers with caller-provided queryResult), or a
// flat folders array (roleFolders pickers, e.g. form-folder pickers).
// `isStructured` marks data that came from getRolesInFolders — its folders are
// real RoleFolders rows, which is the gate for showing the folder info modal.
export const normalizeFolderData = (type, data) => {
    if (!data) return { folders: [], roles: [], isStructured: false };
    if (Array.isArray(data)) {
        return type === 'roles'
            ? { folders: [], roles: data, isStructured: false }
            : { folders: data, roles: [], isStructured: false };
    }
    return {
        folders: data?.allFolders || [],
        // allRoles is the full visibility-filtered list — the root-level
        // `Roles` field only holds unfoldered roles, so it is NOT a fallback.
        roles: data?.allRoles || [],
        isStructured: true,
    };
};

// Display name for a folder row. Some callers pass pre-joined path names
// ("Parent / Child") — inside the tree, show just the leaf segment so
// indentation carries the hierarchy instead of repeating the path.
const leafName = (name) => (name || '').split(' / ').pop();

// Builds the folder tree. Returns { roots, nodeByID, unfiledRoles, hasRoles }
// where each node is { folder, name, children, roles, agg } and `agg` sums
// counts over ALL descendant roles (this folder and below). Excluded roles are
// dropped up front so aggregates and child checks never count them (otherwise
// a folder would advertise roles that can't be expanded into view).
export const buildFolderModel = (folders = [], roles = [], excludeSet = null) => {
    const visibleRoles = excludeSet?.size ? roles.filter(r => !excludeSet.has(r.id)) : roles;
    const nodeByID = new Map();
    folders.forEach(folder => {
        if (!folder?.id || nodeByID.has(folder.id)) return;
        nodeByID.set(folder.id, {
            folder,
            name: leafName(folder.name),
            children: [],
            roles: visibleRoles.filter(r => r.folderID === folder.id),
            agg: { roles: 0, members: 0, permissions: 0, resources: 0 },
        });
    });

    const roots = [];
    nodeByID.forEach(node => {
        const parent = node.folder.parentFolderID ? nodeByID.get(node.folder.parentFolderID) : null;
        if (parent) parent.children.push(node);
        else roots.push(node);
    });

    const byName = (a, b) => (a.name || '').localeCompare(b.name || '');
    const sortTree = (nodes) => {
        nodes.sort(byName);
        nodes.forEach(n => {
            n.roles.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            sortTree(n.children);
        });
    };
    sortTree(roots);

    // Bottom-up aggregates (descendant roles only).
    const aggregate = (node) => {
        node.children.forEach(child => {
            aggregate(child);
            node.agg.roles += child.agg.roles;
            node.agg.members += child.agg.members;
            node.agg.permissions += child.agg.permissions;
            node.agg.resources += child.agg.resources;
        });
        node.agg.roles += node.roles.length;
        node.roles.forEach(r => {
            const c = roleCounts(r);
            node.agg.members += c.members;
            node.agg.permissions += c.permissions;
            node.agg.resources += c.resources;
        });
    };
    roots.forEach(aggregate);

    // Roles whose folderID points nowhere (hidden/archived folder edge cases).
    const unfiledRoles = visibleRoles
        .filter(r => !r.folderID || !nodeByID.has(r.folderID))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    return { roots, nodeByID, unfiledRoles, hasRoles: visibleRoles.length > 0 };
};

const countSelectedDescendants = (node, selectedIDs) => {
    if (!selectedIDs || selectedIDs.size === 0) return 0;
    let count = node.roles.reduce((sum, r) => sum + (selectedIDs.has(r.id) ? 1 : 0), 0);
    node.children.forEach(child => { count += countSelectedDescendants(child, selectedIDs); });
    return count;
};

const roleRow = (role, depth, folderPathText, selectedIDs) => ({
    id: `role-${role.id}`,
    kind: 'role',
    role,
    original: role,
    heading: role.name,
    sub_heading: role.description,
    // Folder-path context line — currently always "" because search keeps the
    // hierarchy (indentation carries the context); retained on the row shape
    // so generic item rendering keeps working if a caller supplies it.
    body: folderPathText || '',
    depth,
    counts: roleCounts(role),
    isDynamic: !!role.quickFilterID,
    isHidden: !!role.isHidden,
    isSelected: selectedIDs.has(role.id),
});

const folderRow = (node, depth, expandedIDs, selectableFolder, selectedIDs, countSelected) => ({
    id: `folder-${node.folder.id}`,
    kind: 'folder',
    folder: node.folder,
    original: node.folder,
    heading: node.name,
    depth,
    agg: node.agg,
    isExpanded: expandedIDs.has(node.folder.id),
    // Folder pickers don't render role rows, so roles alone can't make a
    // folder expandable there — only real child folders can.
    hasChildren: node.children.length > 0 || (!selectableFolder && node.roles.length > 0),
    selectable: selectableFolder,
    isSelected: selectableFolder && selectedIDs.has(node.folder.id),
    selectedDescendants: countSelected ? countSelectedDescendants(node, selectedIDs) : 0,
});

// Flattened visible rows for the hierarchy view (no active search).
// Descendants of collapsed folders stay hidden. Everything starts collapsed
// because expandedIDs starts empty.
const hierarchyRows = ({ model, expandedIDs, selectableFolders, selectedIDs, excludeSet }) => {
    const rows = [];

    // Role rows are omitted entirely in selectableFolders ('roleFolders')
    // pickers: a role row there was selectable, committing a Role where the
    // caller expects a Folder, and its info icon opened folder content with a
    // role id. Folder pickers show folders only.
    const emitRoles = (list, depth) => {
        if (selectableFolders) return;
        list.forEach(role => rows.push(roleRow(role, depth, null, selectedIDs)));
    };

    const emitFolder = (node, depth) => {
        if (excludeSet.has(node.folder.id)) return;
        rows.push(folderRow(node, depth, expandedIDs, selectableFolders, selectedIDs, !selectableFolders));
        if (!expandedIDs.has(node.folder.id)) return;
        node.children.forEach(child => emitFolder(child, depth + 1));
        emitRoles(node.roles, depth + 1);
    };

    model.roots.forEach(node => emitFolder(node, 0));
    emitRoles(model.unfiledRoles, 0);

    return rows;
};

// Filtered hierarchy while searching (Move Folder modal pattern): a folder
// appears when its name matches OR it has any matching descendant (folder
// name, role name/description); a role appears on a name/description match.
// Every emitted folder row is flagged expanded so results render fully open
// regardless of expandedIDs — the tree shape stays visible for context.
const searchRows = ({ model, searchText, selectableFolders, selectedIDs, excludeSet }) => {
    const term = (searchText || '').trim().toLowerCase();
    if (!term) return [];

    const rows = [];
    const roleMatches = (role) => `${role.name || ''} ${role.description || ''}`.toLowerCase().includes(term);
    const nodeMatches = (node) => node.name?.toLowerCase().includes(term);
    // For 'roleFolders' pickers role rows are never rendered, so roles don't
    // count toward a folder's visibility there (a folder with no visible
    // matching content would show with nothing inside it).
    const subtreeMatches = (node) =>
        nodeMatches(node)
        || (!selectableFolders && node.roles.some(roleMatches))
        || node.children.some(subtreeMatches);

    const emitFolder = (node, depth) => {
        if (excludeSet.has(node.folder.id) || !subtreeMatches(node)) return;
        const row = folderRow(node, depth, new Set(), selectableFolders, selectedIDs, !selectableFolders);
        // Force-expanded: the chevron renders open and every matching level is
        // visible at once. Toggling still updates expandedIDs for when the
        // search is cleared.
        row.isExpanded = true;
        rows.push(row);
        node.children.forEach(child => emitFolder(child, depth + 1));
        // Same rule as hierarchyRows: 'roleFolders' pickers never emit role
        // rows. Within a visible folder only matching roles appear.
        if (!selectableFolders) {
            node.roles.filter(roleMatches).forEach(role => rows.push(roleRow(role, depth + 1, null, selectedIDs)));
        }
    };

    model.roots.forEach(node => emitFolder(node, 0));
    if (!selectableFolders) {
        model.unfiledRoles.filter(roleMatches).forEach(role => rows.push(roleRow(role, 0, null, selectedIDs)));
    }
    return rows;
};

// Everything Select-All should commit for a folder picker: all roles for
// 'roles', all folders for 'roleFolders'. Mirrors hierarchy visibility —
// excluded folders and their subtrees are skipped, excluded roles are already
// gone (removed at model build). Descendants of COLLAPSED folders are
// included: select-all covers the model, not just the currently rendered rows.
export const selectableOriginals = ({ model, type, excludeSet = null }) => {
    if (!model) return [];
    const selectableFolders = type === 'roleFolders';
    const items = [];

    const walkNode = (node) => {
        if (excludeSet?.has(node.folder.id)) return;
        if (selectableFolders) items.push(node.folder);
        node.children.forEach(walkNode);
        if (!selectableFolders) items.push(...node.roles);
    };

    model.roots.forEach(walkNode);
    if (!selectableFolders) items.push(...model.unfiledRoles);
    return items;
};

// Main entry point: normalized data + picker state → { rows, model, isStructured }.
export const folderPickerRows = ({ type, data, searchText = '', expandedIDs = new Set(), selectedIDs = new Set(), excludeIds = [], errorFn = null }) => {
    const { folders, roles, isStructured } = normalizeFolderData(type, data);

    const withErrors = errorFn
        ? roles.map(r => {
            const errors = errorFn(r);
            return errors?.length > 0 ? { ...r, errorMessages: errors } : r;
        })
        : roles;

    const excludeSet = new Set(excludeIds || []);
    const model = buildFolderModel(folders, withErrors, excludeSet);
    const selectableFolders = type === 'roleFolders';
    const searching = !!searchText?.trim();

    const rows = searching
        ? searchRows({ model, searchText, selectableFolders, selectedIDs, excludeSet })
        : hierarchyRows({ model, expandedIDs, selectableFolders, selectedIDs, excludeSet });

    return { rows, model, isStructured, searching };
};
